import {
  FLOW_MODE,
  FULFILLMENT_FILE_STATUS,
  FULFILLMENT_LINE_RESULT,
  MARTELET_STATE,
  RUN_STATUS,
  RUN_TYPE_LABELS,
  formatParisDateTime,
} from '@flux/shared';
import { useQuery } from '@tanstack/react-query';
import { Download, RotateCcw } from 'lucide-react';
import type { JSX } from 'react';
import { Page, PageHeader, PageSection } from '@/components/layout/page.js';
import { StatusBadge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { DescriptionItem, DescriptionList } from '@/components/ui/description-list.js';
import { ErrorState } from '@/components/ui/error-state.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table.js';
import { ApiError } from '@/lib/api/client.js';
import { queryKeys } from '@/lib/query/keys.js';
import { downloadFulfillmentRawFile, fetchFulfillmentRunDetail } from './api.js';
import { useRetryFulfillmentLines } from './mutations.js';
import {
  RUNS_BACK_LINK,
  RunCount,
  RunDetailSkeleton,
  RunTimingItems,
} from './run-detail-layout.js';

export interface FulfillmentRunDetailProps {
  runId: string;
}

const TYPE_LABEL = RUN_TYPE_LABELS.fulfillment;

/** `02` §11 : détail d'un run Suivi Martelet — fichiers traités et résultat ligne par ligne. */
export function FulfillmentRunDetailPage({ runId }: FulfillmentRunDetailProps): JSX.Element {
  const query = useQuery({
    queryKey: queryKeys.runs.fulfillmentDetail(runId),
    queryFn: () => fetchFulfillmentRunDetail(runId),
  });
  const retryMutation = useRetryFulfillmentLines();

  if (query.isPending) {
    return (
      <Page width="detail">
        <RunDetailSkeleton tables={2} />
      </Page>
    );
  }

  const errorState = query.isError ? (
    <ErrorState
      message={
        query.error instanceof ApiError ? query.error.message : 'Impossible de charger ce run.'
      }
      detail={query.error instanceof ApiError ? query.error.detail : undefined}
      onRetry={() => {
        void query.refetch();
      }}
    />
  ) : null;

  const run = query.data;
  // Sans données, l'erreur occupe la page ; un rechargement en échec (retour
  // sur l'onglet) laisse en revanche le run déjà chargé visible sous l'erreur.
  if (run === undefined) {
    return (
      <Page width="detail">
        <PageHeader backLink={RUNS_BACK_LINK} title={TYPE_LABEL} />
        {errorState}
      </Page>
    );
  }

  return (
    <Page width="detail">
      <PageHeader
        backLink={RUNS_BACK_LINK}
        title={`Run du ${formatParisDateTime(new Date(run.startedAt))}`}
        status={
          <>
            <StatusBadge registry={FLOW_MODE} code={run.simulation ? 'simulation' : 'active'} />
            <StatusBadge registry={RUN_STATUS} code={run.status} />
          </>
        }
        description={TYPE_LABEL}
      />

      {errorState}

      <PageSection>
        <DescriptionList>
          <RunTimingItems
            startedAt={run.startedAt}
            finishedAt={run.finishedAt}
            trigger={run.trigger}
          />
          <DescriptionItem label="Fichiers">
            <RunCount value={run.files.length} />
          </DescriptionItem>
          <DescriptionItem label="Lignes">
            <RunCount value={run.lines.length} />
          </DescriptionItem>
          <DescriptionItem label="Erreurs">
            <RunCount value={run.stats.errors} alert />
          </DescriptionItem>
          {run.error ? (
            <DescriptionItem label="Erreur">
              <span className="text-status-red-fg">{run.error}</span>
            </DescriptionItem>
          ) : null}
        </DescriptionList>
      </PageSection>

      <PageSection title="Fichiers traités" flush>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nom</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead className="text-right">Lignes</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {run.files.map((file) => (
              <TableRow key={file.id}>
                <TableCell className="whitespace-nowrap">{file.name}</TableCell>
                <TableCell>
                  <StatusBadge registry={FULFILLMENT_FILE_STATUS} code={file.status} />
                </TableCell>
                <TableCell className="text-right tabular-nums">{file.lineCount}</TableCell>
                <TableCell className="text-right">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      downloadFulfillmentRawFile(file.id);
                    }}
                  >
                    <Download aria-hidden="true" />
                    O_COMMANDES brut
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </PageSection>

      <PageSection title="Résultat par ligne" flush>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Commande</TableHead>
              <TableHead>Boutique</TableHead>
              <TableHead>État</TableHead>
              <TableHead>Suivi</TableHead>
              <TableHead>Transporteur déduit</TableHead>
              <TableHead>Résultat</TableHead>
              <TableHead>Erreur</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {run.lines.map((line) => (
              <TableRow key={line.id}>
                <TableCell className="whitespace-nowrap font-medium tabular-nums">
                  {line.orderNumber ?? line.reference ?? '—'}
                </TableCell>
                <TableCell>{line.store ?? '—'}</TableCell>
                <TableCell>
                  {line.marteletState ? (
                    <StatusBadge registry={MARTELET_STATE} code={line.marteletState} />
                  ) : (
                    (line.state ?? '—')
                  )}
                </TableCell>
                <TableCell className="whitespace-nowrap tabular-nums">
                  {line.trackingNumber ?? '—'}
                </TableCell>
                <TableCell>{line.expected?.company ?? '—'}</TableCell>
                <TableCell>
                  <StatusBadge registry={FULFILLMENT_LINE_RESULT} code={line.result} />
                </TableCell>
                <TableCell className="min-w-48">{line.error ?? '—'}</TableCell>
                <TableCell className="text-right">
                  {line.result === 'error' || line.result === 'simulated' ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      isLoading={
                        retryMutation.isPending && retryMutation.variables?.includes(line.id)
                      }
                      onClick={() => {
                        retryMutation.mutate([line.id]);
                      }}
                    >
                      <RotateCcw aria-hidden="true" />
                      Réessayer
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </PageSection>
    </Page>
  );
}
